import engine from '@play-co/timestep-core/lib/ui/engine';
import StateObserver from 'src/StateObserver';
import { getNetworkStats } from '@play-co/timestep-core/lib/ui/resource/stats';
import features from 'src/replicant/ruleset/features';
import { track } from './analytics/utils';

const ANALYTICS_TIMER = 60 * 1000; // Send analytics event timer
const MAX_FRAME_DT = 2 * 1000; // Maximum delta to take into consideration
const START_TRACKING_AFTER = 20 * 1000; // Only log performance data a while after launch

export class PerformanceMetricsTracker {
  public value = {
    min: Number.POSITIVE_INFINITY,
    max: 0,
    total: 0,
  };

  public count: number = 0;

  constructor(public id = '') {}

  public measure(value: number) {
    this.value.total += value;
    ++this.count;

    this.value.min = Math.min(this.value.min, value);
    this.value.max = Math.max(this.value.max, value);
  }

  public getAverage() {
    if (!this.count) {
      return 0;
    }

    return this.value.total / this.count;
  }
}

type GamePerformance = {
  stdDevCount: number;
  stdDevMean: number;
  stdDevSquared: number;
  ticks: number;
  fpsTime: number;
};

export class PerformanceAnalytics {
  private static paused = false;
  // Help to track first event
  private static isFirstEvent = true;

  private static lastTickTime = 0;

  private static session: GamePerformance = {
    stdDevCount: 0,
    stdDevMean: 0,
    stdDevSquared: 0,
    ticks: 0,
    fpsTime: 0,
  };

  private static realtime: GamePerformance = {
    stdDevCount: 0,
    stdDevMean: 0,
    stdDevSquared: 0,
    ticks: 0,
    fpsTime: 0,
  };

  public static init() {
    const state = StateObserver.getState().user;
    if (features(state).amplitudeEventThrottle) return;

    engine.on('Tick', () => this.tick());
    setInterval(() => this.trackAnalytics(), ANALYTICS_TIMER);
  }

  public static pause() {
    this.paused = true;
  }

  public static resume() {
    this.paused = false;
  }

  private static tick() {
    if (this.paused) return;

    const timeNow = performance.now();
    const delta = timeNow - this.lastTickTime;

    // Discard the first tick and any "alt-tabs"
    // Also ignore the first few seconds of the game launching
    if (
      this.lastTickTime !== 0 &&
      delta < MAX_FRAME_DT &&
      timeNow > START_TRACKING_AFTER
    ) {
      this.updateVariance(delta, this.realtime);
      this.updateVariance(delta, this.session);
      this.updateFPS(delta, this.realtime);
      this.updateFPS(delta, this.session);
    }

    this.lastTickTime = timeNow;
  }

  // https://en.wikipedia.org/wiki/Algorithms_for_calculating_variance#Welford's_online_algorithm
  private static updateVariance(value: number, stats: GamePerformance) {
    stats.stdDevCount++;

    const delta = value - stats.stdDevMean;
    stats.stdDevMean += delta / stats.stdDevCount;

    const delta2 = value - stats.stdDevMean;
    stats.stdDevSquared += delta * delta2;
  }

  private static getVariance(stats: GamePerformance): number {
    if (stats.stdDevCount === 0) return 0;

    return stats.stdDevSquared / stats.stdDevCount;
  }

  private static getVarianceSample(stats: GamePerformance): number {
    if (stats.stdDevCount === 0) return 0;

    return stats.stdDevSquared / (stats.stdDevCount - 1);
  }

  private static updateFPS(delta: number, stats: GamePerformance) {
    stats.fpsTime += delta;
    stats.ticks++;
  }

  private static getFPS(stats: GamePerformance): number {
    if (stats.ticks === 0) return 0;

    return 1000 / (stats.fpsTime / stats.ticks);
  }

  private static resetStats(stats: GamePerformance) {
    stats.fpsTime = 0;
    stats.stdDevCount = 0;
    stats.stdDevMean = 0;
    stats.stdDevSquared = 0;
    stats.ticks = 0;
  }

  private static trackAnalytics() {
    if (this.paused) return;

    const memoryInfo = (performance as any)?.memory || {};
    const stats = getNetworkStats();
    const size = +(stats.data.total / 1024 / 1024).toFixed(2);

    track('Performance', {
      // Is this first session event
      isFirst: this.isFirstEvent,

      // Whole session average params
      fps: this.getFPS(this.session),
      choppiness: this.getVariance(this.session),
      choppinessSample: this.getVarianceSample(this.session),

      // Average params between current performance events and previous performance events
      realtimeFPS: this.getFPS(this.realtime),
      realtimeChoppiness: this.getVariance(this.realtime),
      realtimeChoppinessSample: this.getVarianceSample(this.realtime),

      jsHeapSizeLimit: memoryInfo.jsHeapSizeLimit,
      jsHeapSizeTotal: memoryInfo.totalJSHeapSize,
      jsHeapSizeUsed: memoryInfo.usedJSHeapSize,

      dispatchMaxTime: StateObserver.performanceTracker.value.max,
      dispatchAverageTime: StateObserver.performanceTracker.getAverage(),
      dispatchMaxListeners: StateObserver.listenerTracker.value.max,
      dispatchAverageListeners: StateObserver.listenerTracker.getAverage(),

      // All assets loaded during the session
      allAssetsSize: size,
      allAssetsCount: stats.requests.total,
      allAssetsCountOk: stats.requests.status.ok,
    });

    // After each performance event we start collecting statistic again
    this.resetStats(this.realtime);

    // Set first event to false after first event was sent
    this.isFirstEvent = false;
  }
}
